import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getQuota } from '../api';

/**
 * Where Stripe sends people back to. The checkout session's success_url and
 * cancel_url have always pointed at /upgrade/success and /upgrade/cancel, but
 * neither route existed — so anyone who actually paid landed on the SPA
 * catch-all and got the upload form with no confirmation that their money had
 * bought anything.
 *
 * Success does not trust the redirect: the subscription is activated by the
 * Stripe webhook, which may land a moment after the browser does. We re-read
 * the quota and only claim it is live once the API agrees, rather than telling
 * someone they are upgraded because a query string said so.
 */
export function UpgradeSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const r = await getQuota();
        const unlimited = r.quota?.allowed && !Number.isFinite(r.quota?.remaining);
        if (cancelled) return;
        if (unlimited) return setState('active');
      } catch { /* fall through to retry */ }
      if (cancelled) return;
      // The webhook is normally there within a second or two; give it ~10s
      // before telling someone it is still processing.
      if (attempts < 5) setTimeout(poll, 2000);
      else setState('pending');
    };

    if (params.get('session_id')) poll();
    else setState('pending');

    return () => { cancelled = true; };
  }, [params]);

  return (
    <div className="page narrow">
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>{state === 'active' ? '✓' : '⏳'}</div>
        <h1>{state === 'active' ? "You're on unlimited" : 'Payment received'}</h1>
        <p className="lead">
          {state === 'active'
            ? 'Send as many files as you need — no monthly count, any file size, any recipient.'
            : "Thanks. Your upgrade is being activated — it usually takes a few seconds. You can start sending now; if the limit still shows in a minute, refresh."}
        </p>
        <Link className="btn btn-primary" to="/">Send a file</Link>
        <Link className="btn btn-secondary" style={{ marginLeft: 8 }} to="/dashboard">My transfers</Link>
      </div>
    </div>
  );
}

export function UpgradeCancel() {
  return (
    <div className="page narrow">
      <div className="card" style={{ textAlign: 'center' }}>
        <h1>No charge made</h1>
        <p className="lead">
          You closed checkout before paying, so nothing was charged. Your free sends are
          untouched and you can upgrade whenever you like.
        </p>
        <Link className="btn btn-primary" to="/">Back to sending</Link>
      </div>
    </div>
  );
}
